/**
 * Progressive storefront enhancements only. No fetch interception, checkout
 * override, payment-badge relocation, or mutation of native cart markup.
 */
(() => {
  'use strict';
  if (window.__aapkaStorefrontV21) return;
  window.__aapkaStorefrontV21 = true;

  function prepareTables(root) {
    root.querySelectorAll('.shopify-policy__body table, .ae-content-page__body table, .product__description table').forEach((table) => {
      if (table.parentElement?.classList.contains('ae-table-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'ae-table-scroll';
      wrapper.setAttribute('role', 'region');
      const label = table.querySelector('caption')?.textContent?.trim();
      wrapper.setAttribute('aria-label', label || document.title);
      wrapper.tabIndex = 0;
      table.before(wrapper);
      wrapper.append(table);
    });
  }

  function initialize(root = document) {
    prepareTables(root);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

/**
 * v2.4.1 — Explicit video -> native Releasit COD handoff.
 * Only ?ae_cod=1 on a product URL activates this code. Ordinary product-page
 * visits, buy buttons, cart contents and checkout handlers are unchanged.
 * No undocumented app API, cart mutation, form submission or order creation.
 */
(() => {
  'use strict';
  if (window.__aapkaCodHandoffV241) return;
  window.__aapkaCodHandoffV241 = true;

  function startCodHandoff() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('ae_cod') !== '1' || !document.body.classList.contains('template-product')) return;

    // Consume once; back/refresh must not reopen a checkout the customer closed.
    url.searchParams.delete('ae_cod');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);

    const expectedVariant = url.searchParams.get('variant') || '';
    const product = document.querySelector('product-info[id^="MainProduct-"]');
    if (!product || !/^\d+$/.test(expectedVariant)) return;

    const status = document.createElement('p');
    status.className = 'ae-cod-handoff-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = 'Opening Cash on Delivery…';
    const buyButtons = product.querySelector('[data-aapka-buy-buttons]');
    if (buyButtons) buyButtons.before(status);
    else (product.querySelector('.product__info-container') || product).prepend(status);

    const expiresAt = Date.now() + 12000;
    let timer = null;
    let stopped = false;
    let candidate = null;
    let candidateSince = 0;
    let clickedAt = 0;

    function visible(element) {
      if (!element || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    }

    function openForm() {
      const wrapper = document.getElementById('rsi_form_wrapper');
      if (!wrapper || (wrapper.tagName === 'DIALOG' && !wrapper.hasAttribute('open'))) return null;
      return visible(wrapper) && wrapper.querySelector('.rsi-form-main') ? wrapper : null;
    }

    function finish(message = '') {
      if (stopped) return;
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('pointerdown', cancelOnInteraction, true);
      document.removeEventListener('keydown', cancelOnInteraction, true);
      window.removeEventListener('pagehide', cancelOnInteraction);
      if (message) status.textContent = message;
      else status.remove();
    }

    function cancelOnInteraction() {
      // A shopper choosing options, navigating or using a button takes priority
      // over any delayed automatic open. Never surprise them later.
      finish();
    }

    function triggerForProduct() {
      const selector = '[id="rsi_buy_now_button"]';
      const inside = Array.from(product.querySelectorAll(selector));
      const outside = Array.from(document.querySelectorAll(selector)).filter((element) => !product.contains(element));
      return [...inside, ...outside].find((element) => {
        const owner = element.closest('product-info');
        if (owner && owner !== product) return false;
        if (element.closest('[data-aapka-browse-only], .aapka-reel-modal, cart-drawer, cart-notification, #rsi_form_wrapper')) return false;
        if (!visible(element) || element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true' || element.getAttribute('aria-busy') === 'true') return false;
        // Never trigger a native cart submit if an app changes its markup.
        if (element.form && /^(submit|image)$/i.test(element.type || '')) return false;
        return true;
      });
    }

    function tick() {
      if (stopped) return;
      const popup = openForm();
      if (popup) {
        finish();
        if (popup.tagName !== 'DIALOG') popup.scrollIntoView({ block: 'start', behavior: 'auto' });
        return;
      }
      if (document.visibilityState === 'hidden') return;

      if (clickedAt) {
        if (Date.now() - clickedAt > 3000) {
          finish('Cash on Delivery did not open automatically. Please use the Cash on Delivery button on this page.');
        }
        return;
      }
      if (Date.now() > expiresAt) {
        finish('Cash on Delivery is taking longer to load. Please use the Cash on Delivery button on this page once it appears.');
        return;
      }

      // Read the real PDP variant; do not overwrite options or its hidden input.
      const variant = product.querySelector('product-form form input[name="id"]');
      if (!variant) return;
      if (variant.disabled || String(variant.value) !== expectedVariant) {
        finish();
        return;
      }

      const trigger = triggerForProduct();
      if (!trigger) { candidate = null; return; }
      if (trigger !== candidate) {
        candidate = trigger;
        candidateSince = Date.now();
        return;
      }
      // Allow the newly mounted app button to finish binding its own handler.
      if (Date.now() - candidateSince < 200) return;
      clickedAt = Date.now();
      try {
        trigger.click(); // Opens the native form only; never its submit button.
      } catch (error) {
        finish('Please use the Cash on Delivery button on this page to continue.');
      }
    }

    document.addEventListener('pointerdown', cancelOnInteraction, true);
    document.addEventListener('keydown', cancelOnInteraction, true);
    window.addEventListener('pagehide', cancelOnInteraction, { once: true });
    timer = window.setInterval(tick, 200);
    tick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startCodHandoff, { once: true });
  } else {
    startCodHandoff();
  }
})();

/**
 * v2.4.5 — Confirm shopper-initiated exits from the existing Releasit popup.
 * This runs on the storefront only, NOT on Shopify-hosted checkout.
 * No order/cart/network interception, form cloning, customer-data storage,
 * history traps, or undocumented Releasit APIs. The app owns closing/submitting.
 * New/unknown app markup fails open rather than trapping the customer.
 */
(() => {
  'use strict';
  if (window.__aapkaCodExitV245) return;
  window.__aapkaCodExitV245 = true;

  const DIALOG_ID = 'aapka-checkout-exit';
  const CLOSE_SELECTOR = '.rsi-form-close-button';
  const LOGO_SELECTOR = 'a.aapka-header__logo-link, .rsi-form-header a[href]';
  let confirmation = null;
  let pending = null;
  let replaying = false;
  let formObserver = null;
  let stateTimer = null;

  function visible(element) {
    if (!element || !element.isConnected || element.closest('[hidden], [aria-hidden="true"]')) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  function activeForm() {
    if (!document.body?.classList.contains('ae-storefront')) return null;
    const wrapper = document.getElementById('rsi_form_wrapper');
    if (!wrapper || (wrapper.tagName === 'DIALOG' && !wrapper.open) || !visible(wrapper)) return null;
    const panel = wrapper.querySelector('.rsi-form-main');
    const close = Array.from(wrapper.querySelectorAll(CLOSE_SELECTOR)).find(visible);
    const submit = Array.from(wrapper.querySelectorAll('.rsi-submit_button')).find(visible);
    const field = Array.from(wrapper.querySelectorAll(
      'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]), select, textarea'
    )).find((input) => !input.disabled && !input.readOnly && visible(input));
    // A missing order form, thank-you screen, or in-flight submission is not an
    // abandoned editable order. Never infer success from a submit-button click.
    if (!panel || !close || !submit || !field) return null;
    if (submit.matches(':disabled, [aria-disabled="true"], [aria-busy="true"], .loading, .is-loading') ||
        wrapper.getAttribute('aria-busy') === 'true' || panel.getAttribute('aria-busy') === 'true') return null;
    return { wrapper, panel, close };
  }

  function targetElement(event) {
    return event.target instanceof Element ? event.target : event.target?.parentElement;
  }

  function exitAction(event, context) {
    const target = targetElement(event);
    if (!target) return null;
    const close = target.closest(CLOSE_SELECTOR);
    if (close && context.wrapper.contains(close)) return { kind: 'close', trigger: context.close };
    // Native dialog backdrop clicks target the dialog itself, not its panel.
    if (target === context.wrapper && context.wrapper.tagName === 'DIALOG') {
      return { kind: 'close', trigger: context.close };
    }
    const logo = target.closest(LOGO_SELECTOR);
    if (!logo || logo.hasAttribute('download') || (logo.target && logo.target !== '_self')) return null;
    try {
      const destination = new URL(logo.href, window.location.href);
      const headerLogo = document.querySelector('a.aapka-header__logo-link');
      const home = new URL(headerLogo?.href || window.Shopify?.routes?.root || '/', window.location.origin);
      if (destination.origin !== window.location.origin || destination.pathname !== home.pathname) return null;
      return { kind: 'logo', trigger: logo };
    } catch (error) {
      return null;
    }
  }

  function stopWatching() {
    formObserver?.disconnect();
    formObserver = null;
    window.clearInterval(stateTimer);
    stateTimer = null;
  }

  function dismiss(leave = false, restoreFocus = true) {
    const request = pending;
    pending = null;
    stopWatching();
    if (confirmation?.open) confirmation.close();
    if (!request) return;
    if (leave) {
      // Re-read the app state. Do not close a later success/OTP/other screen.
      const context = activeForm();
      if (!context || context.wrapper !== request.wrapper) return;
      const trigger = request.kind === 'close' ? context.close : request.trigger;
      if (!trigger?.isConnected || typeof trigger.click !== 'function') return;
      replaying = true;
      try {
        trigger.click(); // Replay only the original close/logo action, once.
      } finally {
        replaying = false;
      }
    } else if (restoreFocus && visible(request.focus)) {
      request.focus.focus({ preventScroll: true });
    }
  }

  function ensureConfirmation() {
    if (confirmation) return confirmation;
    const dialog = document.createElement('dialog');
    dialog.id = DIALOG_ID;
    dialog.setAttribute('role', 'alertdialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'aapka-exit-title');
    dialog.setAttribute('aria-describedby', 'aapka-exit-description');
    dialog.innerHTML = `
      <div class="ae-exit__panel">
        <button type="button" class="ae-exit__close" data-ae-exit-choice="stay" aria-label="Continue checkout">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
        </button>
        <h2 id="aapka-exit-title">Are you sure you want to exit?</h2>
        <p id="aapka-exit-description">Your order hasn’t been placed yet. You can continue to complete it.</p>
        <div class="ae-exit__actions">
          <button type="button" class="ae-exit__button ae-exit__button--leave" data-ae-exit-choice="leave">Yes, exit checkout</button>
          <button type="button" class="ae-exit__button ae-exit__button--stay" data-ae-exit-choice="stay" autofocus>No, continue checkout</button>
        </div>
      </div>`;
    dialog.addEventListener('click', (event) => {
      event.stopPropagation();
      const control = targetElement(event)?.closest('[data-ae-exit-choice]');
      if (control) {
        event.preventDefault();
        dismiss(control.dataset.aeExitChoice === 'leave');
      } else if (event.target === dialog) {
        // Clicking outside the confirmation means stay, never discard an order.
        dismiss(false);
      }
    });
    dialog.addEventListener('close', () => {
      // Browser/third-party dismissal must not leave a pending replay behind.
      if (pending && !dialog.open) dismiss(false);
    });
    document.body.append(dialog);
    confirmation = dialog;
    return dialog;
  }

  function requestConfirmation(context, action) {
    if (pending) return;
    const dialog = ensureConfirmation();
    pending = { ...action, wrapper: context.wrapper, focus: document.activeElement };
    // Older browsers retain a usable native confirmation rather than a broken
    // custom overlay. The normal app exit is never permanently disabled.
    if (typeof dialog.showModal !== 'function') {
      dismiss(window.confirm('Are you sure you want to exit checkout? Your order has not been placed yet.'));
      return;
    }
    try {
      dialog.showModal(); // A second top-layer dialog sits ABOVE Releasit's one.
      dialog.querySelector('.ae-exit__button--stay').focus({ preventScroll: true });
    } catch (error) {
      dismiss(window.confirm('Are you sure you want to exit checkout? Your order has not been placed yet.'));
      return;
    }
    const checkState = () => {
      if (!pending) return;
      const current = activeForm();
      if (!current || current.wrapper !== pending.wrapper) dismiss(false, false);
    };
    // Observe only the open form and only while this confirmation is showing.
    formObserver = new MutationObserver(checkState);
    formObserver.observe(context.wrapper, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['open', 'hidden', 'class', 'style', 'disabled', 'aria-hidden', 'aria-busy', 'aria-disabled'] });
    // Removal of the whole app wrapper is outside that observer's subtree.
    stateTimer = window.setInterval(checkState, 250);
  }

  function modified(event) {
    return event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.button != null && event.button !== 0);
  }

  // Capture precedes the app's delegated click handlers. Ignore script-driven
  // actions, so successful orders and the app's own dismissal are never blocked.
  window.addEventListener('click', (event) => {
    if (replaying || !event.isTrusted || modified(event) || confirmation?.contains(targetElement(event))) return;
    const context = activeForm();
    if (!context) return;
    const action = exitAction(event, context);
    if (!action) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    requestConfirmation(context, action);
  }, true);

  // Some popup implementations listen on pointerdown/mousedown to dismiss.
  // Stop those early dismissals, but keep the native click/default focus so
  // the click above can ask for confirmation. Never touch form input gestures.
  ['pointerdown', 'mousedown'].forEach((type) => window.addEventListener(type, (event) => {
    if (replaying || !event.isTrusted || modified(event) || pending) return;
    const context = activeForm();
    if (context && exitAction(event, context)) event.stopImmediatePropagation();
  }, true));

  window.addEventListener('keydown', (event) => {
    if (replaying || !event.isTrusted) return;
    if (pending && confirmation?.open) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        dismiss(false);
      } else if (event.key === 'Tab') {
        // Keep the app's own focus trap from reaching past the top dialog.
        event.stopImmediatePropagation();
        const controls = Array.from(confirmation.querySelectorAll('button')).filter(visible);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !confirmation.contains(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !confirmation.contains(document.activeElement))) {
          event.preventDefault(); first?.focus();
        }
      }
      return;
    }
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const context = activeForm();
    if (!context) return;
    // Do not steal Escape from an app-owned secondary dialog such as an OTP
    // prompt or address autocomplete. Native SELECT menus retain Escape too.
    const otherModal = Array.from(document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]'))
      .some((node) => node !== context.wrapper && node !== context.panel && node.id !== DIALOG_ID && !node.contains(context.wrapper) && visible(node));
    const expandedField = targetElement(event)?.closest('select, [role="combobox"][aria-expanded="true"]');
    if (otherModal || expandedField) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    requestConfirmation(context, { kind: 'close', trigger: context.close });
  }, true);

  window.addEventListener('cancel', (event) => {
    if (replaying || !event.isTrusted) return;
    if (event.target === confirmation) {
      event.preventDefault(); event.stopImmediatePropagation(); dismiss(false); return;
    }
    const context = activeForm();
    if (context && event.target === context.wrapper) {
      event.preventDefault(); event.stopImmediatePropagation();
      requestConfirmation(context, { kind: 'close', trigger: context.close });
    }
  }, true);

  window.addEventListener('focusin', (event) => {
    if (pending && confirmation?.contains(targetElement(event))) event.stopImmediatePropagation();
  }, true);
  window.addEventListener('pagehide', () => dismiss(false, false));
})();
